import { normalizePhone } from '@/lib/phone';
import type { CreateOrderInput } from '../types';

const CHECKOUT_ATTEMPT_STORAGE_KEY = 'karebe_checkout_attempt';
const CHECKOUT_SESSION_STORAGE_KEY = 'karebe_checkout_session_id';

export interface CheckoutAttemptState {
  sessionId: string;
  cartFingerprint: string;
  idempotencyKey: string;
  orderId?: string;
  orderNumber?: string | number;
  orderReference?: string;
  updatedAt: string;
}

function createId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.sessionStorage;
}

function normalizeMaybePhone(phone?: string): string {
  if (!phone) {
    return '';
  }

  const normalized = normalizePhone(phone.trim());
  return normalized.success ? normalized.data : phone.trim();
}

function normalizeAddress(input: CreateOrderInput): string {
  return [
    input.deliveryAddress?.street || '',
    input.deliveryAddress?.city || '',
    input.deliveryAddress?.state || '',
    input.deliveryAddress?.postalCode || '',
    input.deliveryAddress?.country || '',
  ]
    .map((value) => value.trim())
    .filter(Boolean)
    .join('|');
}

export function buildCheckoutFingerprint(input: CreateOrderInput): string {
  const normalizedItems = [...input.items]
    .map((item) => ({
      productId: item.productId,
      variantId: item.variantId || '',
      quantity: item.quantity,
      unitPrice: item.unitPrice,
    }))
    .sort((left, right) => {
      const leftKey = `${left.productId}:${left.variantId}`;
      const rightKey = `${right.productId}:${right.variantId}`;
      return leftKey.localeCompare(rightKey);
    });

  return JSON.stringify({
    cartVersion: input.cartVersion || '',
    branchId: input.branchId || '',
    customerName: input.customerName?.trim() || '',
    customerPhone: normalizeMaybePhone(input.phone),
    deliveryMethod: input.deliveryMethod,
    deliveryAddress: normalizeAddress(input),
    notes: input.notes?.trim() || '',
    paymentMethod: input.paymentMethod,
    items: normalizedItems,
  });
}

export function getCheckoutSessionId(): string {
  const storage = getStorage();
  if (!storage) {
    return createId('session');
  }

  const existing = storage.getItem(CHECKOUT_SESSION_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const next = createId('session');
  storage.setItem(CHECKOUT_SESSION_STORAGE_KEY, next);
  return next;
}

function readAttemptState(): CheckoutAttemptState | null {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const raw = storage.getItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as CheckoutAttemptState;
  } catch {
    storage.removeItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
    return null;
  }
}

function writeAttemptState(state: CheckoutAttemptState): CheckoutAttemptState {
  const storage = getStorage();
  storage?.setItem(CHECKOUT_ATTEMPT_STORAGE_KEY, JSON.stringify(state));
  return state;
}

export function getOrCreateCheckoutAttempt(input: CreateOrderInput): CheckoutAttemptState {
  const cartFingerprint = buildCheckoutFingerprint(input);
  const existing = readAttemptState();

  if (existing && existing.cartFingerprint === cartFingerprint) {
    return existing;
  }

  return writeAttemptState({
    sessionId: getCheckoutSessionId(),
    cartFingerprint,
    idempotencyKey: createId('attempt'),
    updatedAt: new Date().toISOString(),
  });
}

export function resolveCheckoutAttempt(
  input: CreateOrderInput,
  resolution: { orderId: string; orderNumber?: string | number; orderReference?: string }
): CheckoutAttemptState {
  const current = getOrCreateCheckoutAttempt(input);

  return writeAttemptState({
    ...current,
    orderId: resolution.orderId,
    orderNumber: resolution.orderNumber,
    orderReference: resolution.orderReference,
    updatedAt: new Date().toISOString(),
  });
}

export function getResolvedCheckoutAttempt(input: CreateOrderInput): CheckoutAttemptState | null {
  const current = getOrCreateCheckoutAttempt(input);
  return current.orderId ? current : null;
}

export function clearCheckoutAttempt(): void {
  getStorage()?.removeItem(CHECKOUT_ATTEMPT_STORAGE_KEY);
}
